import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { useAuth } from '../../context/AuthContext';
import { SearchHit } from '../../services/searchService';
import { SEARCH_MIN_CHARS, useGlobalSearch } from '../../hooks/useGlobalSearch';

type IconName =
  | 'search' | 'box' | 'truck' | 'users' | 'wallet' | 'home' | 'user' | 'grid'
  | 'tag' | 'key' | 'bot' | 'compass' | 'mail' | 'receipt' | 'check' | 'shield';

interface QuickPage {
  label: string;
  description: string;
  route: string;
  icon: IconName;
  adminOnly?: boolean;
}

const QUICK_PAGES: QuickPage[] = [
  { label: 'Overview',          description: 'Dashboard summary',            route: '/overview',           icon: 'home' },
  { label: 'Profile',           description: 'Personal information',         route: '/profile',            icon: 'user' },
  { label: 'Wallet',            description: 'Balance, transactions, cards', route: '/wallet',             icon: 'wallet' },
  { label: 'Shipments',         description: 'Carrier orders',               route: '/orders',             icon: 'box' },
  { label: 'Internal shipments',description: 'In-network deliveries',        route: '/internal-shipments', icon: 'truck' },
  { label: 'New internal shipment', description: 'Book a new internal job',  route: '/internal-shipments/new', icon: 'truck' },
  { label: 'Contacts',          description: 'Saved senders & recipients',   route: '/contacts',           icon: 'users' },
  { label: 'Address validation',description: 'Verify a delivery address',    route: '/address-validation', icon: 'mail' },
  { label: 'API tokens',        description: 'Third-party access',           route: '/api-tokens',         icon: 'key' },
  { label: 'AI shipment',       description: 'Book by chat',                 route: '/ai-shipment',        icon: 'bot' },
  { label: 'Track a parcel',    description: 'Public tracking page',         route: '/track',              icon: 'compass' },
  { label: 'Admin · Users',     description: 'Manage user accounts',         route: '/admin/users',        icon: 'users', adminOnly: true },
  { label: 'Admin · Orders',    description: 'All orders across users',      route: '/admin/orders',       icon: 'grid',  adminOnly: true },
  { label: 'Admin · Settings',  description: 'Application settings',         route: '/admin/settings',     icon: 'shield', adminOnly: true },
];

type FlatItem = { kind: 'page' | 'hit'; key: string; route: string; page?: QuickPage; hit?: SearchHit };

export const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useGlobalSearch(query);

  const isAdmin = Boolean((user as { is_admin?: boolean } | null)?.is_admin);

  const pages = useMemo(
    () => QUICK_PAGES.filter((p) => !p.adminOnly || isAdmin),
    [isAdmin],
  );

  const filteredPages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return pages;
    return pages.filter(
      (p) =>
        p.label.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.route.toLowerCase().includes(q),
    );
  }, [pages, query]);

  // Build the flat ordered list used by keyboard navigation.
  const items = useMemo<FlatItem[]>(() => {
    const list: FlatItem[] = [];
    const isSearching = query.trim().length >= SEARCH_MIN_CHARS;

    if (isSearching && data) {
      data.orders.forEach((hit) =>
        list.push({ kind: 'hit', key: 'order-' + hit.id, route: hit.route, hit }),
      );
      data.shipments.forEach((hit) =>
        list.push({ kind: 'hit', key: 'ship-' + hit.id, route: hit.route, hit }),
      );
      data.contacts.forEach((hit) =>
        list.push({ kind: 'hit', key: 'contact-' + hit.id, route: hit.route, hit }),
      );
    }

    filteredPages.forEach((p) =>
      list.push({ kind: 'page', key: 'page-' + p.route, route: p.route, page: p }),
    );

    return list;
  }, [data, filteredPages, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, data]);

  // Outside click closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Global ⌘K / Ctrl+K shortcut to focus the search input.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Scroll active item into view.
  useEffect(() => {
    if (!open) return;
    const el = dropdownRef.current?.querySelector<HTMLElement>(
      '[data-gs-active="true"]',
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const go = useCallback(
    (route: string) => {
      setOpen(false);
      setQuery('');
      navigate(route);
    },
    [navigate],
  );

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length));
      setOpen(true);
    } else if (e.key === 'Enter') {
      const item = items[activeIndex];
      if (item) {
        e.preventDefault();
        go(item.route);
      }
    } else if (e.key === 'Escape') {
      if (query) {
        setQuery('');
      } else {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
  };

  const trimmed = query.trim();
  const isSearching = trimmed.length >= SEARCH_MIN_CHARS;
  const showHits = isSearching && data;
  const noHits = showHits && data.total === 0;

  let flatIndex = -1;
  const nextIndex = () => ++flatIndex;

  return (
    <div ref={wrapRef} className={`gs-wrap ${open ? 'gs-open' : ''}`}>
      <div className="dash-search gs-input">
        <Icon name="search" size={14} />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder="Search shipments, tracking, contacts…"
          aria-label="Global search"
          role="combobox"
          aria-expanded={open}
          aria-controls="global-search-results"
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            className="gs-clear"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <Icon name="x" size={12} />
          </button>
        )}
        {!query && (
          <span
            className="mono gs-kbd"
            style={{ fontSize: 10, color: 'var(--ink-4)', marginInlineStart: 'auto' }}
          >
            ⌘K
          </span>
        )}
      </div>

      {open && (
        <div id="global-search-results" ref={dropdownRef} className="gs-dropdown" role="listbox">
          {isLoading && (
            <div className="gs-status">
              <Icon name="refresh" size={13} /> Searching…
            </div>
          )}

          {error && (
            <div className="gs-status gs-status-err">
              <Icon name="alertCircle" size={13} /> {error}
            </div>
          )}

          {!isLoading && !error && showHits && data && (
            <>
              {data.orders.length > 0 && (
                <Section title="Shipments" icon="box">
                  {data.orders.map((hit) => {
                    const i = nextIndex();
                    return (
                      <ResultItem
                        key={'o-' + hit.id}
                        hit={hit}
                        active={i === activeIndex}
                        onClick={() => go(hit.route)}
                        onHover={() => setActiveIndex(i)}
                      />
                    );
                  })}
                </Section>
              )}
              {data.shipments.length > 0 && (
                <Section title="Internal shipments" icon="truck">
                  {data.shipments.map((hit) => {
                    const i = nextIndex();
                    return (
                      <ResultItem
                        key={'s-' + hit.id}
                        hit={hit}
                        active={i === activeIndex}
                        onClick={() => go(hit.route)}
                        onHover={() => setActiveIndex(i)}
                      />
                    );
                  })}
                </Section>
              )}
              {data.contacts.length > 0 && (
                <Section title="Contacts" icon="users">
                  {data.contacts.map((hit) => {
                    const i = nextIndex();
                    return (
                      <ResultItem
                        key={'c-' + hit.id}
                        hit={hit}
                        active={i === activeIndex}
                        onClick={() => go(hit.route)}
                        onHover={() => setActiveIndex(i)}
                      />
                    );
                  })}
                </Section>
              )}
              {noHits && (
                <div className="gs-status gs-status-empty">
                  No results for <strong>{trimmed}</strong>
                </div>
              )}
            </>
          )}

          {!error && trimmed.length > 0 && trimmed.length < SEARCH_MIN_CHARS && (
            <div className="gs-status gs-status-empty">
              Keep typing — at least {SEARCH_MIN_CHARS} characters.
            </div>
          )}

          {filteredPages.length > 0 && (
            <Section
              title={isSearching ? 'Pages' : 'Quick access'}
              icon="compass"
            >
              {filteredPages.map((p) => {
                const i = nextIndex();
                return (
                  <PageItem
                    key={p.route}
                    page={p}
                    active={i === activeIndex}
                    onClick={() => go(p.route)}
                    onHover={() => setActiveIndex(i)}
                  />
                );
              })}
            </Section>
          )}

          <div className="gs-foot">
            <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
            <span><kbd>↵</kbd> open</span>
            <span><kbd>esc</kbd> close</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; icon: IconName; children: React.ReactNode }> = ({
  title, icon, children,
}) => (
  <div className="gs-section">
    <div className="gs-section-head">
      <Icon name={icon} size={11} />
      <span>{title}</span>
    </div>
    <div className="gs-section-body">{children}</div>
  </div>
);

const ResultItem: React.FC<{
  hit: SearchHit;
  active: boolean;
  onClick: () => void;
  onHover: () => void;
}> = ({ hit, active, onClick, onHover }) => {
  const iconName: IconName = hit.type === 'order' ? 'box' : hit.type === 'shipment' ? 'truck' : 'users';
  const status = (hit.meta?.status as string | undefined) || '';

  return (
    <button
      type="button"
      className={`gs-item ${active ? 'gs-item-active' : ''}`}
      data-gs-active={active}
      onClick={onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
    >
      <span className="gs-item-icon">
        <Icon name={iconName} size={14} />
      </span>
      <span className="gs-item-text">
        <span className="gs-item-title">{hit.title}</span>
        {hit.subtitle && <span className="gs-item-sub">{hit.subtitle}</span>}
      </span>
      {status && (
        <span className={`gs-tag gs-tag-${status.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
          {status}
        </span>
      )}
      <Icon name="chevronRight" size={12} />
    </button>
  );
};

const PageItem: React.FC<{
  page: QuickPage;
  active: boolean;
  onClick: () => void;
  onHover: () => void;
}> = ({ page, active, onClick, onHover }) => (
  <button
    type="button"
    className={`gs-item ${active ? 'gs-item-active' : ''}`}
    data-gs-active={active}
    onClick={onClick}
    onMouseEnter={onHover}
    onFocus={onHover}
  >
    <span className="gs-item-icon">
      <Icon name={page.icon} size={14} />
    </span>
    <span className="gs-item-text">
      <span className="gs-item-title">{page.label}</span>
      <span className="gs-item-sub">{page.description}</span>
    </span>
    <span className="gs-item-route mono">{page.route}</span>
    <Icon name="chevronRight" size={12} />
  </button>
);

export default GlobalSearch;
